/**
 * A dónde puede llevarte el buscador de la mascota.
 *
 * Un índice escrito a mano y no derivado del menú, y es a propósito: el menú
 * dice cómo se llama cada sitio, y esto tiene que entender cómo lo llama quien
 * lo busca. Nadie escribe «Cobros» cuando quiere ver si le pagaron; escribe
 * «pagos», «ventas» o «stripe». Esos sinónimos no caben en una etiqueta de menú
 * y son justo lo que hace que un buscador sirva.
 *
 * Aquí viven sólo los DESTINOS FIJOS —las secciones y las vistas de la cuenta—,
 * que son los mismos para todo el mundo y no hace falta preguntarle a nadie. El
 * contenido de cada proyecto —contactos, publicaciones, ofertas, páginas— lo
 * busca el servidor y se mezcla con esto en el desplegable; ver
 * `searchProjectContent`.
 */
import type { WorkspaceSection } from '../components/ProjectWorkspace/WorkspaceSidebar'
import { HELP_SITE } from './helpCenter'
import { foldText } from '@webbuilder/shared-types'
import { CATALOGS, type Language, type Translate } from '@webbuilder/i18n'
import type { ProjectSearchResults } from './api'

export type SearchTarget =
  | { kind: 'section'; id: WorkspaceSection }
  | { kind: 'global'; id: 'billing' | 'plans' | 'settings' }
  /**
   * Un destino que no está en el panel.
   *
   * Existe por Ayuda, que vive en un sitio aparte. Buscar «soporte» y no
   * encontrar nada porque la ayuda dejó de ser una vista sería perder lo único
   * que el buscador hacía bien aquí: entender que nadie escribe «Ayuda» cuando
   * lo que quiere es que alguien le conteste.
   */
  | { kind: 'external'; url: string }
  /** El editor del sitio: sale del panel, como «Abrir en el editor». */
  | { kind: 'editor' }
  /** Una ficha concreta dentro de una sección: un contacto, una oferta. */
  | { kind: 'record'; section: WorkspaceSection; id: string }
  /** Un elemento de CMS, que se edita en el editor y no en el panel. */
  | { kind: 'cmsItem'; collectionId: string; itemId: string }

/**
 * De qué clase es un resultado.
 *
 * Es lo que decide bajo qué rótulo cae y con qué icono sale. Sin esto, «Nueva
 * página» y «Páginas» se veían idénticos en la lista y no había forma de saber
 * si uno llevaba a una sección o abría una página concreta.
 */
export type SearchKind = 'destination' | 'page' | 'cms' | 'contact' | 'offer'

export type SearchEntry = {
  label: string
  kind: SearchKind
  /** El detalle a la derecha del nombre: el área, el correo, la colección. */
  group: string
  /** Cómo lo llama la gente. En minúsculas y sin tildes. */
  keywords: string[]
  target: SearchTarget
}

/**
 * Un destino fijo, antes de saber en qué idioma se está mirando.
 *
 * Guarda claves donde `SearchEntry` guarda texto: el índice se construye al
 * cargar el módulo y el idioma se decide después. `searchDashboard` lo resuelve
 * al buscar y devuelve `SearchEntry` ya en el idioma de quien mira.
 */
type Destination = {
  labelKey: string
  kind: SearchKind
  groupKey: string
  /**
   * Los sinónimos, en los dos idiomas — y se empareja contra LOS DOS a la vez.
   *
   * No es un descuido: son términos de búsqueda, no texto de pantalla. Quien
   * lleva el panel en inglés y piensa en español escribe «cobros», y encontrar
   * Payments es exactamente lo que espera; al revés, quien busca «payouts» con
   * el panel en español ya lo encontraba, porque esa palabra estaba en la lista
   * castellana. Emparejar sólo con el idioma activo habría quitado algo que
   * hoy funciona a cambio de nada.
   *
   * Por eso tampoco viven en el catálogo: allí va lo que alguien LEE, y esto no
   * se lee nunca — se teclea.
   */
  keywords: Record<Language, string[]>
  target: SearchTarget
}

/** El orden de los grupos en el desplegable, y con qué clave se llaman. */
export const SEARCH_GROUPS: Array<{ kind: SearchKind; labelKey: string }> = [
  /* Los destinos arriba: quien escribe «cont» casi siempre quiere ir a
     Contactos, no abrir un contacto que se llame así. */
  { kind: 'destination', labelKey: 'find.kind.destination' },
  { kind: 'page', labelKey: 'find.kind.page' },
  { kind: 'cms', labelKey: 'find.kind.cms' },
  { kind: 'contact', labelKey: 'find.kind.contact' },
  { kind: 'offer', labelKey: 'find.kind.offer' },
]

const ENTRIES: Destination[] = [
  // ── El sitio ──────────────────────────────────────────────────────────────
  { labelKey: 'find.pages', kind: 'destination', groupKey: 'find.group.website', target: { kind: 'section', id: 'pages' }, keywords: {
    es: ['paginas', 'pagina', 'inicio', 'home', 'menu', 'navegacion', 'estructura'],
    en: ['pages', 'page', 'home', 'homepage', 'menu', 'navigation', 'structure', 'sitemap'] } },
  { labelKey: 'find.blog', kind: 'destination', groupKey: 'find.group.website', target: { kind: 'section', id: 'blog' }, keywords: {
    es: ['blog', 'articulos', 'entradas', 'posts', 'noticias', 'publicaciones', 'escribir'],
    en: ['blog', 'articles', 'posts', 'news', 'writing', 'stories'] } },
  { labelKey: 'find.cms', kind: 'destination', groupKey: 'find.group.website', target: { kind: 'section', id: 'cms' }, keywords: {
    es: ['cms', 'contenido', 'coleccion', 'colecciones', 'catalogo'],
    en: ['cms', 'content', 'collection', 'collections', 'catalog'] } },
  { labelKey: 'find.forms', kind: 'destination', groupKey: 'find.group.website', target: { kind: 'section', id: 'forms' }, keywords: {
    es: ['formularios', 'formulario', 'mensajes', 'contacto', 'envios', 'respuestas', 'leads'],
    en: ['forms', 'form', 'messages', 'contact', 'submissions', 'replies', 'leads', 'inbox'] } },
  { labelKey: 'find.publish', kind: 'destination', groupKey: 'find.group.website', target: { kind: 'section', id: 'publish' }, keywords: {
    es: ['dominio', 'dominios', 'url', 'direccion', 'publicar', 'publicacion', 'dns', 'ssl', 'comprar dominio'],
    en: ['domain', 'domains', 'url', 'address', 'publish', 'publishing', 'dns', 'ssl', 'buy domain', 'custom domain'] } },

  // ── El dinero ─────────────────────────────────────────────────────────────
  { labelKey: 'find.payments', kind: 'destination', groupKey: 'find.group.sales', target: { kind: 'section', id: 'payments' }, keywords: {
    es: ['ofertas', 'oferta', 'productos', 'precios', 'vender', 'catalogo'],
    en: ['offers', 'offer', 'products', 'prices', 'pricing', 'sell', 'catalog'] } },
  /* Las dos secciones acotadas del catálogo. Van aquí y no sólo bajo «Ofertas»
     porque quien escribe «pdf» o «consultoría» busca su lista, no el catálogo
     entero — y son entradas del menú como cualquier otra. */
  { labelKey: 'find.digitalProducts', kind: 'destination', groupKey: 'find.group.sales', target: { kind: 'section', id: 'digitalProducts' }, keywords: {
    es: ['productos digitales', 'digital', 'pdf', 'archivo', 'archivos', 'descarga', 'descargable', 'ebook', 'plantilla'],
    en: ['digital products', 'digital', 'pdf', 'file', 'files', 'download', 'downloadable', 'ebook', 'template'] } },
  { labelKey: 'find.physicalProducts', kind: 'destination', groupKey: 'find.group.sales', target: { kind: 'section', id: 'physicalProducts' }, keywords: {
    es: ['productos fisicos', 'productos', 'tienda', 'inventario', 'existencias', 'envio', 'mercancia'],
    en: ['physical products', 'products', 'shop', 'store', 'inventory', 'stock', 'merchandise'] } },
  { labelKey: 'find.services', kind: 'destination', groupKey: 'find.group.sales', target: { kind: 'section', id: 'services' }, keywords: {
    es: ['servicios', 'servicio', 'consultoria', 'asesoria', 'trabajo', 'horas', 'proyecto'],
    en: ['services', 'service', 'consulting', 'advisory', 'work', 'hours', 'project'] } },
  { labelKey: 'find.orders', kind: 'destination', groupKey: 'find.group.sales', target: { kind: 'section', id: 'orders' }, keywords: {
    es: ['cobros', 'pagos', 'ventas', 'pedidos', 'compras', 'transacciones'],
    en: ['payments', 'payment', 'charges', 'sales', 'orders', 'purchases', 'transactions'] } },
  { labelKey: 'section.memberships', kind: 'destination', groupKey: 'find.group.sales', target: { kind: 'section', id: 'memberships' }, keywords: {
    es: ['membresias', 'membresia', 'suscripciones', 'paquetes', 'paquete de clases', 'socios', 'mensualidad'],
    en: ['memberships', 'membership', 'subscriptions', 'packs', 'class packs', 'members', 'monthly'] } },
  { labelKey: 'section.collections', kind: 'destination', groupKey: 'find.group.sales', target: { kind: 'section', id: 'collections' }, keywords: {
    es: ['colecciones', 'coleccion', 'categorias', 'categoria', 'grupos de productos', 'etiquetas'],
    en: ['collections', 'collection', 'categories', 'category', 'product groups', 'tags'] } },
  { labelKey: 'find.coupons', kind: 'destination', groupKey: 'find.group.sales', target: { kind: 'section', id: 'coupons' }, keywords: {
    es: ['cupones', 'cupon', 'descuentos', 'descuento', 'codigo promocional', 'promociones', 'ofertas especiales'],
    en: ['coupons', 'coupon', 'discounts', 'discount', 'promo code', 'promotion code', 'promotions'] } },
  { labelKey: 'find.shipping', kind: 'destination', groupKey: 'find.group.sales', target: { kind: 'section', id: 'shipping' }, keywords: {
    es: ['envios', 'envio', 'paqueteria', 'tarifas de envio', 'recoger en tienda', 'paises', 'existencias', 'inventario'],
    en: ['shipping', 'delivery', 'carrier', 'shipping rates', 'local pickup', 'countries', 'stock', 'inventory'] } },
  { labelKey: 'find.payouts', kind: 'destination', groupKey: 'find.group.sales', target: { kind: 'section', id: 'payouts' }, keywords: {
    es: ['depositos', 'pagos recibidos', 'transferencias', 'banco', 'retiros', 'payouts'],
    en: ['payouts', 'payout', 'deposits', 'transfers', 'bank', 'withdrawals'] } },
  /* «Servicios con agenda» en el menú, pero se busca por «reservas» y por
     «citas»: el nombre dice qué son y las palabras dicen para qué se usan. */
  { labelKey: 'find.bookings', kind: 'destination', groupKey: 'find.group.sales', target: { kind: 'section', id: 'bookings' }, keywords: {
    es: ['reservas', 'citas', 'agenda', 'calendario', 'horarios', 'turnos', 'servicios con agenda', 'servicios reservables'],
    en: ['bookings', 'booking', 'appointments', 'schedule', 'calendar', 'availability', 'slots', 'scheduled services'] } },
  { labelKey: 'find.stripe', kind: 'destination', groupKey: 'find.group.sales', target: { kind: 'section', id: 'stripe' }, keywords: {
    es: ['stripe', 'pasarela', 'tarjetas', 'cobrar', 'conectar stripe'],
    en: ['stripe', 'gateway', 'cards', 'card payments', 'connect stripe'] } },

  // ── Los números ───────────────────────────────────────────────────────────
  /* Las tres viven en la carpeta Analíticas del menú, así que aquí comparten
     sinónimo: quien las busca por «analíticas» encuentra las tres. Y se llaman
     «Analíticas de …» y no «Ventas» o «Reservas» a secas, como en el menú: un
     nombre que coincide exacto gana a una palabra clave, y «¿dónde veo mis
     ventas?» llevaba a los números en vez de a la lista de cobros. */
  { labelKey: 'find.analytics', kind: 'destination', groupKey: 'find.group.analytics', target: { kind: 'section', id: 'analytics' }, keywords: {
    es: ['analiticas', 'analitica', 'visitas', 'trafico', 'estadisticas', 'metricas', 'paises', 'visitantes'],
    en: ['analytics', 'visits', 'traffic', 'stats', 'statistics', 'metrics', 'countries', 'visitors'] } },
  { labelKey: 'find.salesAnalytics', kind: 'destination', groupKey: 'find.group.analytics', target: { kind: 'section', id: 'salesAnalytics' }, keywords: {
    es: ['analiticas', 'analiticas de ventas', 'ingresos', 'facturacion del sitio', 'ticket medio', 'conversion'],
    en: ['analytics', 'sales analytics', 'revenue', 'income', 'average order', 'conversion'] } },
  { labelKey: 'find.bookingsAnalytics', kind: 'destination', groupKey: 'find.group.analytics', target: { kind: 'section', id: 'bookingsAnalytics' }, keywords: {
    es: ['analiticas', 'analiticas de reservas', 'citas', 'agenda', 'asistencia', 'no show', 'inasistencias', 'cancelaciones', 'ocupacion'],
    en: ['analytics', 'booking analytics', 'appointments', 'schedule', 'attendance', 'no show', 'no shows', 'cancellations', 'occupancy'] } },

  // ── La gente ──────────────────────────────────────────────────────────────
  { labelKey: 'find.contacts', kind: 'destination', groupKey: 'find.group.people', target: { kind: 'section', id: 'contacts' }, keywords: {
    es: ['contactos', 'clientes', 'personas', 'suscriptores', 'agenda de clientes'],
    en: ['contacts', 'customers', 'clients', 'people', 'subscribers', 'address book'] } },
  { labelKey: 'find.team', kind: 'destination', groupKey: 'find.group.people', target: { kind: 'section', id: 'team' }, keywords: {
    es: ['equipo', 'usuarios', 'invitar', 'permisos', 'colaboradores', 'roles'],
    en: ['team', 'users', 'invite', 'permissions', 'collaborators', 'roles', 'seats'] } },

  // ── La cuenta ─────────────────────────────────────────────────────────────
  { labelKey: 'find.billing', kind: 'destination', groupKey: 'find.group.account', target: { kind: 'global', id: 'billing' }, keywords: {
    es: ['facturacion', 'facturas', 'suscripcion', 'membresia', 'tarjeta', 'cobros de veltica', 'recibos'],
    en: ['billing', 'invoices', 'subscription', 'membership', 'card', 'veltica charges', 'receipts'] } },
  /* Global y no de sección: la membresía es de la persona, así que se llega a
     ella igual con un proyecto abierto que sin ninguno. */
  { labelKey: 'find.plans', kind: 'destination', groupKey: 'find.group.account', target: { kind: 'global', id: 'plans' }, keywords: {
    es: ['planes', 'plan', 'precios', 'mejorar', 'upgrade', 'pro', 'business'],
    en: ['plans', 'plan', 'pricing', 'upgrade', 'pro', 'business'] } },
  { labelKey: 'find.usage', kind: 'destination', groupKey: 'find.group.account', target: { kind: 'section', id: 'usage' }, keywords: {
    es: ['uso', 'limites', 'consumo', 'almacenamiento', 'ancho de banda', 'cuota'],
    en: ['usage', 'limits', 'consumption', 'storage', 'bandwidth', 'quota'] } },
  { labelKey: 'find.settings', kind: 'destination', groupKey: 'find.group.account', target: { kind: 'global', id: 'settings' }, keywords: {
    es: ['usuario', 'perfil', 'cuenta', 'contrasena', 'correo', 'ajustes', 'foto'],
    en: ['user', 'profile', 'account', 'password', 'email', 'settings', 'photo', 'avatar'] } },
  { labelKey: 'find.help', kind: 'destination', groupKey: 'find.group.account', target: { kind: 'external', url: HELP_SITE }, keywords: {
    es: ['ayuda', 'soporte', 'contactar', 'dudas', 'preguntas', 'documentacion', 'guias', 'tutoriales'],
    en: ['help', 'support', 'contact us', 'questions', 'docs', 'documentation', 'guides', 'tutorials'] } },

  // ── Los ajustes del proyecto ──────────────────────────────────────────────
  { labelKey: 'find.general', kind: 'destination', groupKey: 'find.group.settings', target: { kind: 'section', id: 'general' }, keywords: {
    es: ['configuracion', 'ajustes del sitio', 'nombre', 'favicon', 'idioma'],
    en: ['settings', 'site settings', 'name', 'favicon', 'language'] } },
  { labelKey: 'find.business', kind: 'destination', groupKey: 'find.group.settings', target: { kind: 'section', id: 'business' }, keywords: {
    es: ['negocio', 'empresa', 'datos fiscales', 'rfc', 'direccion fiscal', 'telefono'],
    en: ['business', 'company', 'tax details', 'tax id', 'vat', 'billing address', 'phone'] } },
  { labelKey: 'find.policies', kind: 'destination', groupKey: 'find.group.settings', target: { kind: 'section', id: 'policies' }, keywords: {
    es: ['politicas', 'privacidad', 'terminos', 'cookies', 'legal', 'reembolsos'],
    en: ['policies', 'privacy', 'terms', 'cookies', 'legal', 'refunds'] } },
  { labelKey: 'find.region', kind: 'destination', groupKey: 'find.group.settings', target: { kind: 'section', id: 'region' }, keywords: {
    es: ['region', 'moneda', 'pais', 'zona horaria', 'impuestos'],
    en: ['region', 'currency', 'country', 'time zone', 'timezone', 'taxes'] } },
  { labelKey: 'find.backups', kind: 'destination', groupKey: 'find.group.settings', target: { kind: 'section', id: 'backups' }, keywords: {
    es: ['copias', 'backup', 'respaldo', 'restaurar', 'version anterior'],
    en: ['backups', 'backup', 'restore', 'previous version', 'snapshots'] } },
  { labelKey: 'find.googleAnalytics', kind: 'destination', groupKey: 'find.group.settings', target: { kind: 'section', id: 'googleAnalytics' }, keywords: {
    es: ['google analytics', 'ga4', 'seguimiento', 'etiqueta'],
    en: ['google analytics', 'ga4', 'tracking', 'tag'] } },
  { labelKey: 'section.config', kind: 'destination', groupKey: 'find.group.settings', target: { kind: 'section', id: 'config' }, keywords: {
    es: ['configuracion', 'ajustes', 'opciones', 'preferencias del sitio'],
    en: ['settings', 'configuration', 'options', 'preferences'] } },
  { labelKey: 'section.transfer', kind: 'destination', groupKey: 'find.group.settings', target: { kind: 'section', id: 'transfer' }, keywords: {
    es: ['transferir', 'traspasar', 'traspaso', 'cambiar de dueno', 'regalar sitio', 'entregar sitio'],
    en: ['transfer', 'handover', 'change owner', 'give site', 'move site'] } },
  { labelKey: 'section.calendars', kind: 'destination', groupKey: 'find.group.settings', target: { kind: 'section', id: 'calendars' }, keywords: {
    es: ['google calendar', 'calendario', 'calendarios', 'outlook', 'apple', 'icloud', 'ical', 'sincronizar', 'agenda'],
    en: ['google calendar', 'calendar', 'calendars', 'outlook', 'apple', 'icloud', 'ical', 'sync', 'schedule'] } },
  { labelKey: 'section.openTable', kind: 'destination', groupKey: 'find.group.settings', target: { kind: 'section', id: 'openTable' }, keywords: {
    es: ['opentable', 'open table', 'reservaciones', 'reservas', 'mesas', 'restaurante'],
    en: ['opentable', 'open table', 'reservations', 'tables', 'restaurant'] } },
  { labelKey: 'section.omnisend', kind: 'destination', groupKey: 'find.group.settings', target: { kind: 'section', id: 'omnisend' }, keywords: {
    es: ['omnisend', 'email marketing', 'boletin', 'newsletter', 'integracion'],
    en: ['omnisend', 'email marketing', 'newsletter', 'integration'] } },

  { labelKey: 'find.editor', kind: 'destination', groupKey: 'find.group.website', target: { kind: 'editor' }, keywords: {
    es: ['editor', 'abrir editor', 'editar', 'editar sitio', 'editar pagina', 'disenar', 'diseno', 'constructor', 'lienzo'],
    en: ['editor', 'open editor', 'edit', 'edit site', 'edit page', 'design', 'builder', 'canvas'] } },

  // ── El resto del menú ─────────────────────────────────────────────────────
  { labelKey: 'section.overview', kind: 'destination', groupKey: 'find.group.website', target: { kind: 'section', id: 'overview' }, keywords: {
    es: ['inicio', 'resumen', 'panel', 'dashboard', 'principal', 'casa'],
    en: ['home', 'overview', 'dashboard', 'start', 'main'] } },
  { labelKey: 'section.bookingsCalendar', kind: 'destination', groupKey: 'find.group.sales', target: { kind: 'section', id: 'bookingsCalendar' }, keywords: {
    es: ['citas', 'calendario', 'agenda', 'proximas citas', 'reservas de hoy', 'clases'],
    en: ['appointments', 'calendar', 'schedule', 'upcoming', 'today bookings', 'classes'] } },
  { labelKey: 'section.emails', kind: 'destination', groupKey: 'find.group.apps', target: { kind: 'section', id: 'emails' }, keywords: {
    es: ['bandeja', 'correos', 'correo', 'inbox', 'mensajes', 'email'],
    en: ['inbox', 'emails', 'email', 'mail', 'messages'] } },
  { labelKey: 'section.emailMarketing', kind: 'destination', groupKey: 'find.group.apps', target: { kind: 'section', id: 'emailMarketing' }, keywords: {
    es: ['marketing', 'campanas', 'boletin', 'newsletter', 'correos masivos'],
    en: ['marketing', 'campaigns', 'newsletter', 'bulk email'] } },
  { labelKey: 'section.courses', kind: 'destination', groupKey: 'find.group.apps', target: { kind: 'section', id: 'courses' }, keywords: {
    es: ['cursos', 'curso', 'clases en linea', 'lecciones', 'alumnos', 'academia'],
    en: ['courses', 'course', 'online classes', 'lessons', 'students', 'academy'] } },
]

/**
 * En qué carpeta del menú lateral vive cada destino. Se busca también por su
 * nombre: quien escribe «website» o «catálogo» busca lo que hay dentro de esa
 * carpeta, y antes no salía nada porque la carpeta no era un destino.
 */
const MENU_GROUP_KEYS: Partial<Record<string, string>> = {
  pages: 'nav.group.website', blog: 'nav.group.website', cms: 'nav.group.website', forms: 'nav.group.website', publish: 'nav.group.website', brand: 'nav.group.website',
  payments: 'nav.group.catalog', digitalProducts: 'nav.group.catalog', physicalProducts: 'nav.group.catalog', services: 'nav.group.catalog', bookings: 'nav.group.catalog', bookingsCalendar: 'nav.group.catalog',
  orders: 'nav.group.sales', coupons: 'nav.group.sales', shipping: 'nav.group.sales', payouts: 'nav.group.sales', stripe: 'nav.group.sales',
  analytics: 'nav.group.analytics', salesAnalytics: 'nav.group.analytics', bookingsAnalytics: 'nav.group.analytics', googleAnalytics: 'nav.group.analytics',
  emails: 'nav.group.apps', emailMarketing: 'nav.group.apps', courses: 'nav.group.apps',
  config: 'nav.config', general: 'nav.config', business: 'nav.config', region: 'nav.config', policies: 'nav.config', backups: 'nav.config', usage: 'nav.config', transfer: 'nav.config', omnisend: 'nav.config',
}

/** El nombre de la carpeta en los dos idiomas, sin tildes. */
function menuGroupWords(target: SearchTarget): string[] {
  const key = target.kind === 'section' ? MENU_GROUP_KEYS[target.id] : undefined
  if (!key) return []
  return [CATALOGS.es[key], CATALOGS.en[key]].filter((word): word is string => Boolean(word)).map((word) => normalize(word))
}

/**
 * Sin tildes y en minúsculas: quien busca «analiticas» tiene que encontrarlas.
 *
 * Comparte `foldText` con el resto de buscadores del editor y del panel, para
 * que todos se comporten igual. Además protege la ñ, que aquí se caía: al
 * descomponer, «año» se volvía «ano» y buscar eso lo encontraba.
 */
const normalize = foldText

/**
 * Los destinos que casan con lo escrito, del más al menos evidente.
 *
 * Tres grados de coincidencia y en este orden: el nombre empieza por lo escrito,
 * una de sus palabras clave empieza por lo escrito, o aparece en medio de
 * cualquiera de las dos. Sin esa gradación, escribir «pa» sacaba «Copias de
 * seguridad» —que contiene «pa» en «respaldo»— por delante de «Páginas».
 */
export function searchDashboard(query: string, t: Translate, language: Language, limit = 6): SearchEntry[] {
  return rankDestinations(query, t, language).slice(0, limit).map((item) => item.entry)
}

/** El asistente suma las coincidencias: nombre y sinónimo exactos antes que prefijos. */
export function rankDestinations(query: string, t: Translate, language: Language): Array<{ entry: SearchEntry; score: number }> {
  const raw = normalize(query).trim()
  if (raw.length < 2) return []
  /* Y en singular: «websites», «cupones», «paginas» tienen que encontrar lo
     mismo que sin la ese. */
  const needles = [raw]
  if (raw.length > 3 && raw.endsWith('es')) needles.push(raw.slice(0, -2))
  if (raw.length > 3 && raw.endsWith('s')) needles.push(raw.slice(0, -1))

  const scored: Array<{ entry: SearchEntry; score: number }> = []
  for (const destination of ENTRIES) {
    /* El nombre, en el idioma de quien mira: es lo que va a leer en la lista y
       lo que probablemente esté escribiendo. Los sinónimos, en los dos. */
    const label = t(destination.labelKey)
    const folded = normalize(label)
    const words = [...destination.keywords.es, ...destination.keywords.en]

    const groups = menuGroupWords(destination.target)

    let score = 0
    for (const needle of needles) {
      let found = 0
      if (folded === needle) found = 5
      else if (words.some((word) => normalize(word) === needle)) found = 4
      else if (folded.startsWith(needle)) found = 3
      /* La carpeta del menú cuenta como un sinónimo más. */
      else if (words.some((word) => word.startsWith(needle)) || groups.some((group) => group.startsWith(needle))) found = 2
      else if (folded.includes(needle) || words.some((word) => word.includes(needle))) found = 1
      score = Math.max(score, found)
    }
    if (score === 0) continue

    scored.push({
      entry: {
        label,
        kind: destination.kind,
        group: t(destination.groupKey),
        keywords: words,
        target: destination.target,
      },
      score,
    })
  }

  return scored.sort((a, b) => b.score - a.score || a.entry.label.localeCompare(b.entry.label, language))
}

/** El identificador con que el asistente nombra un destino ante la IA. */
export function destinationId(target: SearchTarget): string | null {
  if (target.kind === 'section') return target.id
  if (target.kind === 'global') return `global:${target.id}`
  if (target.kind === 'external') return 'help'
  if (target.kind === 'editor') return 'editor'
  return null
}

/**
 * Cómo llama la gente a un destino, en pocas palabras: para la IA del
 * asistente, que sólo veía el nombre. «Cobros» a secas no le decía que ahí
 * están las ventas, y «¿dónde veo mis ventas?» acababa en las analíticas.
 */
export function destinationHint(id: string, language: Language): string {
  const entry = ENTRIES.find((destination) => destinationId(destination.target) === id)
  return entry ? entry.keywords[language].slice(0, 4).join(', ') : ''
}

/**
 * Todos los destinos fijos, en el idioma de quien mira: la lista que el
 * asistente le enseña a la IA para que elija UNO de ellos y no se invente
 * secciones. `allowed` quita lo que el rol de quien pregunta no puede abrir.
 */
export function dashboardDestinations(t: Translate, allowed: (target: SearchTarget) => boolean): Array<{ id: string; label: string; target: SearchTarget }> {
  const list: Array<{ id: string; label: string; target: SearchTarget }> = []
  for (const destination of ENTRIES) {
    const id = destinationId(destination.target)
    if (!id || !allowed(destination.target)) continue
    list.push({ id, label: t(destination.labelKey), target: destination.target })
  }
  return list
}

/** El contenido que encontró el servidor, como resultados del buscador. */
export function projectContentEntries(found: ProjectSearchResults, t: Translate): SearchEntry[] {
  return [
    ...found.contacts.map((contact) => ({
      label: contact.name || contact.email || t('search.contact'),
      kind: 'contact' as const,
      /* El correo bajo el nombre: con dos «Juan Pérez» es lo único que
         los distingue. */
      group: contact.email || t('search.contacts'),
      keywords: [],
      target: { kind: 'record' as const, section: 'contacts' as const, id: contact.id },
    })),
    ...found.cmsItems.map((item) => ({
      label: item.title || t('common.untitled'),
      kind: 'cms' as const,
      group: item.collectionName,
      keywords: [],
      target: { kind: 'cmsItem' as const, collectionId: item.collectionId, itemId: item.id },
    })),
    ...found.offers.map((offer) => ({
      label: offer.title,
      kind: 'offer' as const,
      group: offer.status === 'active' ? t('search.offerActive') : t('search.offer'),
      keywords: [],
      target: { kind: 'record' as const, section: 'payments' as const, id: offer.id },
    })),
    ...found.pages.map((page) => ({
      label: page.name || page.slug,
      kind: 'page' as const,
      group: `/${page.slug.replace(/^\/+/, '')}`,
      keywords: [],
      target: { kind: 'record' as const, section: 'pages' as const, id: page.pageId },
    })),
  ]
}
